import { useState, useEffect } from "react";
import { Search, User as UserIcon, FileText, Activity, Layers, DollarSign } from 'lucide-react';
import { SkeletonLoader } from '../components/ui/SkeletonLoader';
import { formatDistanceToNow } from "date-fns";
import { supabase } from "../lib/supabase";
import type { ClaimsActivityLog, Profile } from "../types/database.types";
import { useToast } from "../components/ui/ToastModal";
import { logError } from "../lib/logError";
import { fetchAllRows } from "../lib/fetchAllRows";

// Helper to get relative time
const formatRelativeTime = (dateString: string | null) => {
  if (!dateString) return "";
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
};

// Helper for entity icons
const getEntityIcon = (type: string) => {
  switch (type) {
    case "PROJECT":
      return <Layers size={16} />;
    case "CATEGORY":
    case "SUBCATEGORY":
      return <FileText size={16} />;
    case "CLAIM":
      return <Activity size={16} />; // Or Chart Icon
    case "VARIATION":
      return <DollarSign size={16} />;
    default:
      return <Activity size={16} />;
  }
};

export function ActivityLogsPage() {
  const [logs, setLogs] = useState<
    (ClaimsActivityLog & {
      user?: Profile | null;
      project?: { job_location: string } | null;
    })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<Profile[]>([]);
  const [filterUser, setFilterUser] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const { addToast } = useToast();
  // Track if we have any logs at all (to show/hide filters)
  const [hasAnyLogs, setHasAnyLogs] = useState(false);

  useEffect(() => {
    // Initial load
    fetchUsers();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  useEffect(() => {
    // Refetch on filter change (if we have logs or filters are active)
    if (hasAnyLogs || filterUser || filterType) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, filterType]);

  const fetchUsers = async () => {
    // Only fetch users who have activity
    // Paged: 1,684 log rows today against a 1,000-row response cap, so an
    // unpaged read dropped users who only appear in the older rows.
    let logData: { user_id: string | null }[] = [];
    try {
      logData = await fetchAllRows((afterId, limit) => {
        let query = supabase
          .from("claims_activity_logs")
          .select("id, user_id")
          .order("id", { ascending: true })
          .limit(limit);
        if (afterId) query = query.gt("id", afterId);
        return query;
      });
    } catch (err) {
      logError("Error fetching activity log users:", err);
    }

    if (logData.length > 0) {
      const uniqueUserIds = Array.from(
        new Set(
          logData
            .map((l) => l.user_id)
            .filter((id): id is string => Boolean(id)),
        ),
      );

      if (uniqueUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("*")
          .in("id", uniqueUserIds);

        if (profiles) setUsers(profiles);
      }
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("claims_activity_logs")
        .select(`*, user:profiles(*), project:claims_projects(job_location)`)
        .order("created_at", { ascending: false })
        .limit(100);

      if (filterUser) query = query.eq("user_id", filterUser);
      if (filterType) query = query.eq("entity_type", filterType);

      const { data, error } = await query;

      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setLogs((data as any) || []);

      // Update global state - if we have data and NO filters, then we have history
      if (!filterUser && !filterType && data && data.length > 0) {
        setHasAnyLogs(true);
      }
    } catch (err) {
      logError("Error fetching logs:", err);
      addToast({
        type: "error",
        title: "Error",
        message: "Failed to load activity logs",
      });
    } finally {
      setLoading(false);
    }
  };

  // Group and Filter Logic (Same as before)
  const groupedLogs = logs.reduce(
    (acc, log) => {
      if (!log.created_at) return acc;
      const date = new Date(log.created_at).toDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(log);
      return acc;
    },
    {} as Record<string, typeof logs>,
  );

  const filteredGroups = Object.entries(groupedLogs).reduce(
    (acc, [date, groupLogs]) => {
      const filtered = groupLogs.filter((log) =>
        log.description.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      if (filtered.length > 0) acc[date] = filtered;
      return acc;
    },
    {} as Record<string, typeof logs>,
  );

  return (
    <div className="container" style={{ paddingTop: "var(--spacing-xl)" }}>
      {/* Header */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
          Activity Feed
        </h1>
        <p style={{ color: "var(--text-muted)" }}>
          Tracking all changes across projects
        </p>
      </div>

      {/* Filters - Only show if we know there are logs or filters are active */}
      {(hasAnyLogs || filterUser || filterType) && (
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-md)",
            padding: "var(--spacing-lg)",
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-md)",
            marginBottom: "var(--spacing-xl)",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 400 }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-muted)",
              }}
            />
            <input
              type="text"
              className="form-input"
              placeholder="Search activity..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 34, width: "100%" }}
            />
          </div>

          <select
            className="form-input"
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            style={{ width: "auto", minWidth: 150 }}
          >
            <option value="">All Users</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.email}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ width: "auto", minWidth: 150 }}
          >
            <option value="">All Types</option>
            <option value="PROJECT">Projects</option>
            <option value="CATEGORY">Categories</option>
            <option value="CLAIM">Claims</option>
            <option value="VARIATION">Variations</option>
          </select>
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <SkeletonLoader variant="activity" count={8} />
      ) : Object.keys(filteredGroups).length === 0 ? (
        <div style={{ textAlign: "center", padding: "var(--spacing-xl)" }}>
          <p style={{ color: "var(--text-muted)" }}>No activity found.</p>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-xl)",
          }}
        >
          {Object.entries(filteredGroups)
            .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
            .map(([date, groupLogs]) => (
              <div key={date}>
                <h3
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: "var(--spacing-md)",
                    borderBottom: "1px solid var(--border-color)",
                    paddingBottom: "var(--spacing-sm)",
                  }}
                >
                  {date === new Date().toDateString() ? "Today" : date}
                </h3>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--spacing-sm)",
                  }}
                >
                  {groupLogs.map((log) => (
                    <div
                      key={log.id}
                      className="card fade-in"
                      style={{
                        padding: "var(--spacing-md)",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "var(--spacing-md)",
                        transition: "transform 0.1s",
                      }}
                    >
                      {/* User Avatar */}
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "var(--bg-tertiary)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--text-primary)",
                          fontWeight: 600,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {log.user?.full_name?.charAt(0).toUpperCase() || (
                          <UserIcon size={14} />
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div>
                            <p
                              style={{
                                fontWeight: 500,
                                fontSize: "var(--font-size-md)",
                                margin: 0,
                              }}
                            >
                              {log.description}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                marginTop: 4,
                                fontSize: "var(--font-size-sm)",
                                color: "var(--text-muted)",
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  background: "var(--bg-secondary)",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                }}
                              >
                                {getEntityIcon(log.entity_type)}
                                {log.entity_type}
                              </span>
                              <span>•</span>
                              {log.project?.job_location && (
                                <>
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      background: "rgba(30, 115, 190, 0.1)",
                                      color: "#1e73be",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {log.project.job_location}
                                  </span>
                                  <span>•</span>
                                </>
                              )}
                              <span>
                                {log.user?.full_name || "Unknown User"}
                              </span>
                              <span>•</span>
                              <span>{formatRelativeTime(log.created_at)}</span>
                            </div>
                          </div>

                          {log.metadata && (
                            <div
                              style={{
                                fontSize: 11,
                                background: "var(--bg-tertiary)",
                                padding: "2px 6px",
                                borderRadius: 4,
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "var(--text-muted)",
                              }}
                            >
                              METADATA
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
